(() => {
  const selectRegularPrice = (product, productId, variantId, businessPrice) => {
    if (String(product?.id) !== String(productId) || !Array.isArray(product?.variants)) return null;
    const variant = product.variants.find(item => String(item.id) === String(variantId));
    if (!variant || !Number.isSafeInteger(variant.price) || !Number.isSafeInteger(businessPrice) || businessPrice < 0 || variant.price <= businessPrice) return null;
    return variant.price;
  };
  const initializeRegularPrices = (root) => {
    const nodes = [...root.querySelectorAll('[data-b2b-regular-price]')];
    if (!nodes.length || !window.fetch) return;
    const queue = [];
    const products = new Map();
    let active = 0;
    let currencyCheck;
    const readJson = async (url) => {
      const response = await fetch(url, {credentials: 'omit', headers: {'Accept': 'application/json'}, cache: 'no-store', signal: AbortSignal.timeout(10000)});
      if (!response.ok) throw new Error('reference_price_unavailable');
      return response.json();
    };
    const show = async (node) => {
      node.hidden = true;
      node.dataset.referenceState = 'loading';
      try {
        const url = new URL(node.dataset.productPriceUrl, window.location.origin);
        if (url.origin !== window.location.origin || !/\/products\/[^/]+\.js$/.test(url.pathname) || url.search || url.hash) throw new Error('reference_url_invalid');
        const prefix = url.pathname.slice(0, url.pathname.indexOf('/products/'));
        currencyCheck ||= readJson(prefix + '/cart.js');
        if ((await currencyCheck).currency !== 'SEK') throw new Error('reference_currency_mismatch');
        if (!products.has(url.href)) products.set(url.href, readJson(url.href));
        const product = await products.get(url.href);
        const price = selectRegularPrice(product, node.dataset.productId, node.dataset.variantId, Number(node.dataset.businessPrice));
        if (price === null) throw new Error('reference_price_not_higher');
        node.querySelector('[data-b2b-regular-value]').textContent = new Intl.NumberFormat(document.documentElement.lang || 'sv', {style: 'currency', currency: 'SEK', currencyDisplay: 'code'}).format(price / 100);
        node.hidden = false;
        node.dataset.referenceState = 'ready';
      } catch {
        node.hidden = true;
        node.dataset.referenceState = 'unavailable';
      }
    };
    const drain = () => {
      while (active < 3 && queue.length) {
        const node = queue.shift();
        active++;
        show(node).finally(() => {active--; drain();});
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          const node = entry.target.querySelector('[data-b2b-regular-price]');
          if (node) queue.push(node);
        }
        drain();
      }, {rootMargin: '400px'});
      nodes.forEach(node => observer.observe(node.closest('[data-b2b-product]') || node.parentElement));
    } else {
      queue.push(...nodes);
      drain();
    }
  };
  const initialize = (root) => {
    if (root.dataset.qtB2bInitialized === 'true') return;
    root.dataset.qtB2bInitialized = 'true';
    if (root.dataset.b2bCatalogTarget && root.dataset.b2bDesignMode !== 'true') {
      const target = new URL(root.dataset.b2bCatalogTarget, window.location.origin);
      const allowed = [...root.querySelectorAll('[data-b2b-catalog-link]')].some((link) => {
        const url = new URL(link.href, window.location.origin);
        return url.origin === window.location.origin && url.pathname === target.pathname;
      });
      if (allowed && target.origin === window.location.origin && target.searchParams.get('view') === 'b2b' && target.searchParams.get('sort_by') === 'best-selling') {
        const current = new URL(window.location.href);
        const page = current.searchParams.get('page');
        if (root.dataset.b2bCatalogPreservePage === 'true' && /^[1-9]\d*$/.test(page || '')) target.searchParams.set('page', page);
        const preview = current.searchParams.get('preview_theme_id');
        if (/^\d+$/.test(preview || '')) target.searchParams.set('preview_theme_id', preview);
        if (target.pathname !== current.pathname || current.searchParams.get('sort_by') !== 'best-selling' || current.searchParams.get('view') !== 'b2b') window.location.replace(target.href);
      }
      return;
    }
    initializeRegularPrices(root);
    const status = root.querySelector('[data-b2b-status]');
    const tray = root.querySelector('[data-b2b-selection]');
    const add = root.querySelector('[data-b2b-selection-add]');
    const rows = [...root.querySelectorAll('[data-b2b-item]')];
    if (!status || !tray || !add || !rows.length || !window.fetch) return;
    const minimum = 5;
    const selectionTotal = entries => entries.reduce((total, entry) => total + entry.quantity, 0);
    const validSelection = entry => entry && typeof entry.id === 'string' && /^\d{6,20}$/.test(entry.id) && Number.isSafeInteger(entry.quantity) && entry.quantity > 0 && typeof entry.title === 'string' && entry.title.length <= 400;
    const withinRule = (quantity, inCart, min, increment, max) => quantity === 0 || (Number.isSafeInteger(quantity) && quantity > 0 && inCart + quantity >= min && (inCart + quantity) % increment === 0 && (max === null || inCart + quantity <= max));
    const storageKey = 'quicktest:b2b-selection:v1:' + root.dataset.b2bSelectionScope;
    const selected = new Map();
    const details = root.querySelector('[data-b2b-selection-details]');
    const list = root.querySelector('[data-b2b-selection-list]');
    const count = root.querySelector('[data-b2b-selection-count]');
    const progress = root.querySelector('[data-b2b-selection-progress]');
    let busy = false;
    let locked = false;
    let storageAvailable = true;
    const announce = (message, error = false) => { status.dataset.error = String(error); status.textContent = message; };
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored) && stored.length <= 250) stored.filter(validSelection).forEach(entry => selected.set(entry.id, entry));
    } catch { storageAvailable = false; }
    const persist = () => {
      try { sessionStorage.setItem(storageKey, JSON.stringify([...selected.values()])); }
      catch { storageAvailable = false; announce(root.dataset.selectionStorage, true); }
    };
    const rowInput = row => row.querySelector('[data-b2b-quantity]');
    const rule = input => ({inCart: Number(input.dataset.cartQuantity), min: Number(input.dataset.ruleMin), increment: Number(input.dataset.ruleIncrement), max: input.dataset.ruleMax === '' ? null : Number(input.dataset.ruleMax)});
    const updateControls = row => {
      const input = rowInput(row), limits = rule(input), value = Number(input.value);
      input.readOnly = busy || locked;
      row.querySelector('[data-b2b-minus]').disabled = busy || locked || input.disabled || !Number.isFinite(value) || value <= 0;
      row.querySelector('[data-b2b-plus]').disabled = busy || locked || input.disabled || (limits.max !== null && limits.inCart + value + limits.increment > limits.max);
    };
    const render = () => {
      const total = selectionTotal([...selected.values()]);
      const invalid = rows.some(row => rowInput(row).getAttribute('aria-invalid') === 'true');
      add.disabled = busy || locked || invalid || total < minimum;
      tray.setAttribute('aria-busy', String(busy));
      progress.textContent = total >= minimum ? root.dataset.selectionReady : root.dataset.selectionRemaining.replace('__count__', String(minimum - total));
      count.textContent = root.dataset.selectionCount.replace('__count__', String(total));
      details.hidden = total === 0;
      list.replaceChildren();
      for (const entry of selected.values()) {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = entry.quantity + ' × ' + entry.title;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', root.dataset.selectionRemove.replace('__product__', entry.title));
        remove.disabled = busy || locked;
        remove.addEventListener('click', () => {
          selected.delete(entry.id);
          rows.filter(row => row.dataset.variantId === entry.id).forEach(row => {rowInput(row).value = '0'; rowInput(row).removeAttribute('aria-invalid'); updateControls(row);});
          persist(); render(); details.querySelector('summary').focus();
        });
        item.append(label, remove); list.append(item);
      }
    };
    const syncInput = (row, paint = true) => {
      const input = rowInput(row), quantity = Number(input.value), limits = rule(input);
      const valid = input.value.trim() !== '' && withinRule(quantity, limits.inCart, limits.min, limits.increment, limits.max);
      input.setAttribute('aria-invalid', String(!valid));
      if (valid && quantity > 0 && !input.disabled) selected.set(row.dataset.variantId, {id: row.dataset.variantId, quantity, title: row.dataset.productTitle});
      else selected.delete(row.dataset.variantId);
      updateControls(row);
      if (paint) {persist(); render();}
    };
    rows.forEach(row => {
      const input = rowInput(row), saved = selected.get(row.dataset.variantId);
      if (saved && !input.disabled) input.value = String(saved.quantity);
      else if (saved) selected.delete(row.dataset.variantId);
      input.addEventListener('input', () => syncInput(row));
      input.addEventListener('change', () => {
        if (input.value === '') input.value = '0';
        syncInput(row);
        if (input.getAttribute('aria-invalid') === 'true') announce(root.dataset.quantityInvalid, true);
      });
      input.addEventListener('keydown', event => {if (event.key === 'Enter') {event.preventDefault(); if (!add.disabled) add.click();}});
      row.querySelector('[data-b2b-plus]').addEventListener('click', () => {
        const limits = rule(input), value = Number(input.value) || 0;
        input.value = String(value === 0 ? Math.max(limits.increment, limits.min - limits.inCart) : value + limits.increment);
        syncInput(row);
      });
      row.querySelector('[data-b2b-minus]').addEventListener('click', () => {
        const limits = rule(input), value = Number(input.value) || 0, next = value - limits.increment;
        input.value = String(next < Math.max(limits.increment, limits.min - limits.inCart) ? 0 : next);
        syncInput(row);
      });
      syncInput(row, false);
    });
    persist();
    if (!storageAvailable) announce(root.dataset.selectionStorage, true);
    const refresh = async () => {
      const response = await fetch(root.dataset.cartUrl, {credentials: 'same-origin', headers: {'Accept': 'application/json'}, cache: 'no-store', signal: AbortSignal.timeout(15000)});
      if (!response.ok) throw new Error('cart_read_failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) throw new Error('cart_read_invalid');
      for (const row of rows) {
        const input = rowInput(row), limits = rule(input);
        const quantity = cart.items.filter(item => String(item.variant_id) === row.dataset.variantId).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        input.dataset.cartQuantity = String(quantity);
        input.disabled = limits.max !== null && limits.max - quantity < limits.increment;
        if (limits.max !== null) input.max = String(Math.max(0, limits.max - quantity));
        const product = row.closest('[data-b2b-product]'), inCart = product.querySelector('[data-b2b-in-cart]');
        inCart.hidden = quantity === 0;
        inCart.textContent = root.dataset.inCartTemplate.replace('__quantity__', String(quantity));
        product.querySelector('[data-b2b-limit]').hidden = !input.disabled;
        if (!withinRule(Number(input.value), quantity, limits.min, limits.increment, limits.max)) input.setAttribute('aria-invalid','true');
        else input.removeAttribute('aria-invalid');
        updateControls(row);
      }
      root.querySelectorAll('[data-b2b-cart-count]').forEach(node => {node.textContent = root.dataset.countTemplate.replace('__count__', String(cart.item_count));});
      return cart;
    };
    add.addEventListener('click', async () => {
      if (busy || locked || selectionTotal([...selected.values()]) < minimum) return;
      if (rows.some(row => rowInput(row).getAttribute('aria-invalid') === 'true')) {announce(root.dataset.quantityInvalid,true); return;}
      busy = true; render(); rows.forEach(updateControls); announce(root.dataset.selectionAdding);
      let attempted = false;
      try {
        await refresh();
        if (rows.some(row => rowInput(row).getAttribute('aria-invalid') === 'true')) {announce(root.dataset.quantityChanged,true); return;}
        const items = [...selected.values()].filter(validSelection).map(({id,quantity}) => ({id,quantity}));
        if (selectionTotal(items) < minimum) return;
        selected.clear();
        persist();
        if (!storageAvailable) {locked = true; announce(root.dataset.selectionStorage,true); return;}
        rows.forEach(row => {rowInput(row).value = '0';});
        attempted = true;
        const response = await fetch(root.dataset.cartAdd, {method:'POST',credentials:'same-origin',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({items}),signal:AbortSignal.timeout(20000)});
        const result = await response.json();
        if (!response.ok || result.status >= 400) {
          locked = true;
          announce((typeof result.description === 'string' ? result.description + ' ' : '') + root.dataset.selectionReview,true);
          try {await refresh();} catch {}
          return;
        }
        const cart = await refresh();
        announce(root.dataset.selectionAdded);
        root.dispatchEvent(new CustomEvent('quicktest:b2b-cart-added',{bubbles:true,detail:{itemCount:cart.item_count}}));
        root.dispatchEvent(new CustomEvent('wubs:cart-updated',{bubbles:true,detail:{item_count:cart.item_count}}));
      } catch {
        if (attempted) locked = true;
        announce(attempted ? root.dataset.selectionReview : root.dataset.refreshFailed,true);
      } finally {
        busy = false; rows.forEach(updateControls); render();
      }
    });
    render();

  };
  const scan = (scope) => scope.querySelectorAll('[data-qt-b2b]').forEach(initialize);
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
